import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { usePageTitle } from "@/lib/usePageTitle";
import { api, rows } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { JobCard } from "@/components/JobCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const JOB_TYPES = ["full_time", "part_time", "contract", "daily_wage", "internship"];

export default function JobsList() {
  const { t, lang, tenant } = useApp();
  usePageTitle(t("jobs.title")); // RF-21: per-page tab title
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState(null);
  const [query, setQuery] = useState(params.get("q") || "");
  const jobType = params.get("job_type") || "";

  useEffect(() => {
    if (!tenant) return;
    setJobs(null);
    api
      .get("/city/jobs", {
        params: { tenant_id: tenant.id, limit: 100, q: params.get("q") || undefined, job_type: jobType || undefined },
      })
      .then((r) => setJobs(rows(r.data)));
  }, [tenant, params, jobType]);

  const applySearch = () => {
    const next = new URLSearchParams(params);
    if (query) next.set("q", query);
    else next.delete("q");
    setParams(next);
  };

  const setJobType = (type) => {
    const next = new URLSearchParams(params);
    if (type) next.set("job_type", type);
    else next.delete("job_type");
    setParams(next);
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8">
      <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl">{t("jobs.title")}</h1>
      <p className="mt-2 max-w-2xl text-base text-muted-foreground">
        {t("jobs.subtitle", { city: localized(lang, tenant, "name") })}
      </p>

      <div className="mt-8 flex flex-col gap-4">
        <div className="flex max-w-xl items-center gap-2 rounded-full border border-border bg-card p-2">
          <Search size={18} className="ml-3 text-muted-foreground" />
          <Input
            data-testid="jobs-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applySearch()}
            placeholder={t("common.searchPlaceholder")}
            className="border-0 bg-transparent shadow-none focus-visible:ring-0"
          />
          <Button className="rounded-full px-5" data-testid="jobs-search-btn" onClick={applySearch}>
            {t("common.search")}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="job-type-filters">
          <button
            onClick={() => setJobType("")}
            data-testid="job-type-filter-all"
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors duration-200 ${
              !jobType ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border"
            }`}
          >
            {t("common.all")}
          </button>
          {JOB_TYPES.map((type) => (
            <button
              key={type}
              onClick={() => setJobType(type)}
              data-testid={`job-type-filter-${type}`}
              className={`rounded-full border px-4 py-1.5 text-sm transition-colors duration-200 ${
                jobType === type ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border"
              }`}
            >
              {t(`jobs.types.${type}`)}
            </button>
          ))}
        </div>
      </div>

      {jobs === null ? (
        <p className="mt-8 text-center text-muted-foreground">{t("common.loading")}</p>
      ) : jobs.length === 0 ? (
        <p
          className="mt-6 rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground"
          data-testid="jobs-empty"
        >
          {t("common.noResults")}
        </p>
      ) : (
        <>
          <p className="mt-8 text-sm font-medium text-muted-foreground" data-testid="jobs-result-count">
            {t("jobs.resultCount", { count: jobs.length })}
          </p>
          <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="jobs-grid">
            {jobs.map((j) => (
              <JobCard key={j.id} job={j} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
